class ChlaChart {
  constructor(config) {
    this.container = config.container;
    this.data = config.data || [];
    this.xAxis = config.xAxis || {};
    this.yAxis = config.yAxis || {};
    this.events = config.events || {};
    this.heightScale = config.heightScale || 0.2;
    this.widthScale = config.widthScale || 0.98;
    this.color = index => d3.schemeSet1[index];
  }

  /**
   * This function is to check if the user has provided values
   * for a required config property
   * @param {*} prop
   */
  checkConfigProp(prop) {
    if (typeof prop.value === 'undefined') {
      throw `Error: Please provide a value for the ChlaChart "${prop.key}" config option `;
    }
  }

  /**
   * This function is used to determine if the config
   * object is properly configured for creating the graph
   */
  validateConfig() {
    // required config options
    const configProps = [
      { key: 'container', value: this.container },
      { key: 'data', value: this.data },
    ];
    configProps.forEach(prop => this.checkConfigProp(prop));

    return this;
  }

  /**
   * This method is used to set data for the Heatmap
   * @param {array} data [data for heatmap]
   */
  setData(data) {
    this.data = data;
    return this;
  }

  /**
   * This method is used to set the chart dimensions
   * @param {string} selector  [CSS selector for chart]
   * @param {object} [margin] [margins to apply]
   * @return {object} [chart dimensions]
   */
  setChartDimensions(selector = this.container, margin = { top: 15, right: 50, bottom: 25, left: 100 }) {
    let width = d3.select(selector).style('width');
    width = parseInt(width.substring(0, width.length - 2)) * this.widthScale;

    let height = width * this.heightScale;

    // Subtract margins from width and height
    (width = width - margin.left - margin.right), (height = height - margin.top - margin.bottom);

    const chartDimensions = {
      margin: margin,
      width: width,
      height: height,
    };
    this.chartDimensions = chartDimensions;
    return chartDimensions;
  }

  /**
   * This method is used to generate the tooltip HTML
   * @param {*} data
   */
  setTooltipHTML(data) {
    if (data.length > 0) {
      let html = `<div class="ts-tooltip-title">${data[0].prettyDate}</div>`;

      html += `
          
          <div class="ts-tooltip-value-wrapper">
            <div class="ts-tooltip-legend" style="background-color: ${this.color(1)}"></div>
            <div class="ts-tooltip-value">TN:TP: ${data[0].tn_tp}</div>
          </div>
          <div class="ts-tooltip-value-wrapper">
            <div class="ts-tooltip-legend" style="background-color: ${this.color(2)}"></div>
            <div class="ts-tooltip-value">TDN:TDP: ${data[0].tdn_tdp}</div>
          </div>
          <div class="ts-tooltip-value-wrapper">
            <div class="ts-tooltip-legend" style="background-color: ${this.color(4)}"></div>
            <div class="ts-tooltip-value">TIN:SRP: ${data[0].tin_srp}</div>
          </div>
          <div class="ts-tooltip-value-wrapper">
            <div class="ts-tooltip-legend" style="background-color: ${this.color(3)}"></div>
            <div class="ts-tooltip-value">Limitation: ${data[0].lmt}</div>
          </div>
          <div class="ts-tooltip-value-wrapper">
            <div class="ts-tooltip-legend" style="background-color: ${this.color(0)}"></div>
            <div class="ts-tooltip-value">Chla: ${data[0].chla || 'N/A'}</div>
          </div>
          
          
          `;

      return html;
    } else {
      const existingHTML = document.querySelector(`.tooltip-card-${this.container.replace('#', '')}`).innerHTML;
      return existingHTML;
    }
  }

  /**
   * This function is used to create the graph tooltip
   * @param {*} self
   * @param {*} scale
   * @param {*} tooltipBar
   * @param {*} tooltipLine
   * @param {*} tooltipCard
   */
  createTooltip(self, scale, tooltipBar, tooltipLine, tooltipCard) {
    // grab the location of the users cursor and convert it to a meaningful x-axis value
    const x0 = parseInt(d3.mouse(self)[0]);
    const { width, height, margin } = this.chartDimensions;
    const { data } = this;

    let activeData = [];

    // let activeData;
    // let sidebarTitle;
    // let gageAbbrev;

    const date = scale.invert(d3.mouse(self)[0]);

    const formatDate = d3.timeFormat('%Y-%m-%d');
    const prettyDate = d3.timeFormat('%b %d, %Y');
    const formattedDate = formatDate(date);
    activeData = data.filter(d => d.rdate === formattedDate);

    activeData = activeData.map(d => {
      let obj = Object.assign({}, d);
      obj.prettyDate = prettyDate(new Date(`${d.rdate} 00:00:00`));

      return obj;
    });

    // The tooltip bar is appended to the graph when it is rendered
    // This sets the coordinates of the bar as well as the display styles
    tooltipBar
      .attr('x1', x0)
      .attr('x2', x0)
      .attr('y1', 0)
      .attr('y2', height)
      .style('stroke', '#5d86ff')
      .style('stroke-width', '36px')
      .style('opacity', 0.1);

    // The tooltip line is appended to the graph when it is rendered
    // This sets the coordinates of the line as well as the display styles
    tooltipLine
      .attr('x1', x0)
      .attr('x2', x0)
      .attr('y1', 0)
      .attr('y2', height)
      .style('stroke', '#5d86ff')
      .style('stroke-width', '1px')
      .style('opacity', 0.8);

    if (activeData.length > 0) {
      tooltipCard
        .style('left', d3.event.pageX + 'px')
        .style('top', d3.event.pageY - 28 + 'px')
        .style('opacity', 1)
        .html(this.setTooltipHTML(activeData));
    }
  }

  /**
   * This method is used to remove an existing Heatmap tooltip
   * @return {object} [returns Heatmap]
   */
  removeTooltip(tooltipCard) {
    tooltipCard.style('opacity', 0);
    return this;
  }

  /**
   * This method is used to remove an existing Heatmap
   * @return {object} [returns Heatmap]
   */
  removeSVG(message = '') {
    document.querySelector(this.container).innerHTML = message;
    return this;
  }

  /**
   * This method is used to create the SVG that all of the other chart elements will be appended to
   * @return {object} [returns Heatmap]
   */
  createSVG() {
    const chartDimensions = this.setChartDimensions();
    const { width, height, margin } = chartDimensions;

    this.removeSVG();
    // this.removeTooltip();

    const svg = d3
      .select(this.container)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + 20)
      .attr('id', 'd3-graph')
      .style('overflow', 'visible')
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
    this.svg = svg;
    return this;
  } // createSVG

  /**
   * This method is used to calculate the min/max values for the upstream to downstream dataset
   * The method collapses the values across all value fields into an array for each record
   * That array is then flattened into a single array that the d3.extent method is then run on
   * @param  {array} data [data to evaluate]
   * @return {array}      [returns an array of the min and max]
   */
  calcExtent(data, field) {
    const minField0 = d3.min(data, d => d[field[0]]);
    const minField1 = d3.min(data, d => d[field[1]]);
    const minField2 = d3.min(data, d => d[field[2]]);

    const maxField0 = d3.max(data, d => d[field[0]]);
    const maxField1 = d3.max(data, d => d[field[1]]);
    const maxField2 = d3.max(data, d => d[field[2]]);

    let min = Math.min(minField0, minField1, minField2);
    let max = Math.max(maxField0, maxField1, maxField2) * 1.1;

    // let max = d3.max(data, d => d[field]);
    // if ( min > 0 ) min = 0;
    return [min, max];
  }

  /**
   * This method is used to create the graph scales
   */
  createScales() {
    const { width, height } = this.chartDimensions;
    const data = this.data;

    // Set X and Y ranges
    const x = d3.scaleTime().range([5, width - 5]);
    const y0 = d3.scaleLinear().range([height, 0]);
    const y1 = d3.scaleLinear().range([height, 0]);
    const xAxisFormat = this.xAxis.format || 'mm/dd/yyyy';

    // Set X and Y domains aka the min/max x and y axis values
    x.domain(d3.extent(data, d => new Date(`${d.rdate} 00:00:00`)));

    y0.domain(this.calcExtent(data, ['tn_tp', 'tdn_tdp', 'tin_srp']));
    y1.domain([0, d3.max(data, d => d.chla * 1.1)]);

    this.x = x;
    this.y0 = y0;
    this.y1 = y1;
    return this;
  }

  /**
   * This method is used to create a x or y axis for the heatmap
   * @param  {string}  type                [axis type i.e. X or Y]

   * @param  {String}  [axisClass='label'] [css class(es) to apply to axis]
   * @return {object}                     [returns Heatmap]
   */
  createAxis(type, axisClass) {
    const { width, height } = this.chartDimensions;
    const svg = this.svg;
    const x = this.x;
    const y0 = this.y0;
    const y1 = this.y1;

    if (type === 'x') {
      const axis = this.svg.select(axisClass);
      const xAxisFormat = this.xAxis.format || 'mm/dd/yyyy';

      let tickFormat = d3.timeFormat('%m/%y');

      const xAxis = g =>
        g
          .attr('class', axisClass)
            .style('font-size', '18px')
          .attr('transform', `translate(0,${height})`)
          .call(d3.axisBottom(x).tickFormat(tickFormat));

      // Create the x axis gridlines
      const xAxisGridlines = g =>
        g
          .attr('class', 'x-axis grid')
          .attr('transform', `translate(0,${height})`)
          .call(d3.axisBottom(x).tickSize(-height).tickFormat(''));

      svg.append('g').call(xAxisGridlines);
      svg.append('g').call(xAxis);
    } else if (type === 'y0') {
      const axis = this.svg.select(axisClass);

      const yAxisLeft = label => g =>
        g
          .attr('class', axisClass)
            .style('font-size', '18px')
          .call(d3.axisLeft(y0))
          .append('text')
          .attr('class', 'axis-label')
          .attr('transform', 'rotate(-90)')
          .attr('y', 0 - 65)
          .attr('x', 0 - height / 2)
          .attr('dy', '1em')
          .style('text-anchor', 'middle')
          .text(label);

      const yAxisGridlines = g => g.attr('class', 'y-axis grid').call(d3.axisLeft(y0).tickSize(-width).tickFormat(''));

      svg.append('g').call(yAxisGridlines);
      svg.append('g').call(yAxisLeft('Nitrogen to Phosphorus Ratio'));
    } else if (type === 'y1') {
      const axis = this.svg.select(axisClass);

      const yAxisRight = label => g =>
        g
          .attr('class', axisClass)
            .style('font-size', '18px')
          .attr('transform', 'translate( ' + width + ', 0 )')
          .call(d3.axisRight(y1))
          .append('text')
          .attr('class', 'axis-label chla')
          .attr('transform', 'rotate(-90)')
          .attr('y', 0 + 40)
          .attr('x', 0 - height / 2)
          .attr('dy', '1em')
          .style('text-anchor', 'middle')
          .text(label);

      svg.append('g').call(yAxisRight('Chlorophyll-a ( ug/L )'));
    }
    return this;
  } // createAxis

  /**
   * this method is used to create the graph series
   */
  createSeries() {
    const { width, height } = this.chartDimensions;
    const svg = this.svg;
    let data = this.data;
    const x = this.x;
    const y0 = this.y0;
    const y1 = this.y1;
    const self = this;

    data = data.map(x => {
      let y = { ...x };
      if (y.tdn_tdp === 'NaN') y.tdn_tdp = null;
      if (y.tin_srp === 'NaN') y.tin_srp = null;
      if (y.tn_tp === 'NaN') y.tn_tp = null;
      if (y.chla === 'NaN') y.chla = null;
      return y;
    });

    // console.log(data);
    const tin_srp = this.createLine('rdate', 'tin_srp', x, y0);
    const tdn_tdp = this.createLine('rdate', 'tdn_tdp', x, y0);
    const tn_tp = this.createLine('rdate', 'tn_tp', x, y0);
    const lmt = this.createLine('rdate', 'lmt', x, y0);
    const chla = this.createLine('rdate', 'chla', x, y1);
    // const tin_srp = this.createLine('rdate', 'tin_srp', x, y);

    // Add an empty tooltip bar and line
    const tooltipBar = svg.append('line').attr('class', 'tooltip-bar');
    const tooltipLine = svg.append('line').attr('class', 'tooltip-line');
    const tooltipCard = d3
      .select('body')
      .append('div')
      .attr('class', `tooltip-card tooltip-card-${this.container.replace('#', '')}`);
    // const contextMenu = d3.select('body').append('div').attr('class', 'context-menu');

    // const testCard = d3.select('body').append('div').attr('class', 'tooltip-test').style('opacity', 0);

    const tn_tpSeries = this.appendSeries(svg, data, 'line', tn_tp, this.color(1));
    const tdn_tdpSeries = this.appendSeries(svg, data, 'line', tdn_tdp, this.color(2));
    const tin_srpSeries = this.appendSeries(svg, data, 'line', tin_srp, this.color(4));
    const lmtSeries = this.appendSeries(svg, data, 'ref-line', lmt, this.color(3));
    const chla_series = this.appendSeries(
      svg,
      data.filter(item => item.chla !== null),
      'chla-line',
      chla,
      this.color(0)
    );

    const tn_tpDots = this.createDots(
      svg,
      data.filter(item => item.chla !== null),
      'tn_tp',
      this.color(1),
      x,
      y0
    );
    const tdn_tdpDots = this.createDots(svg, data, 'tdn_tdp', this.color(2), x, y0);
    const tin_srpDots = this.createDots(svg, data, 'tin_srp', this.color(4), x, y0);
    const chlaDots = this.createDots(svg, data, 'chla', this.color(0), x, y1);

    // Add an overlay to the chart so we can append the tooltip lines on hover
    const rect = svg
      .append('rect')
      .attr('transform', 'translate(0,0)')
      .attr('class', 'overlay')
      .style('opacity', 0)
      .attr('width', width)
      .attr('height', height)
      .on('mousemove', function () {
        self.createTooltip(this, x, tooltipBar, tooltipLine, tooltipCard);
      })

      .on('mouseout', function () {
        self.removeTooltip(tooltipCard);
      });

    return this;
  }

  /**
   * This utility method is used as a shorthand for appending a line or area series to the chart
   * @param  {object} svg      [svg to append to]
   * @param  {array} data      [data to plot]
   * @param  {string} classList [list of classes to assign the series]
   * @param  {function} series    [D3 line/area constructor to be used to plot the data]
   * @return {object}           [Returns DOM/SVG node]
   */
  appendSeries(svg, data, classList, series, color) {
    return svg
      .append('path')
      .datum(data)
      .attr('class', classList)
      .attr('d', series)
      .style('stroke', color)
      .style('stroke-width', 2)
      .style('fill', 'none');
  }

  createDots(svg, data, series, color, x, y) {
    return (
      svg
        .selectAll('dot')
        // .data(data)
        .data(data.filter(d => d[series] !== null))
        .enter()
        .append('circle')
        .attr('r', 3)
        .style('stroke', color)
        .style('fill', color)
        .attr('cx', function (d) {
          return x(new Date(d.rdate));
        })
        .attr('cy', function (d) {
          return y(d[series]);
        })
    );
    // .on('mouseover', function (d) {
    //   testCard.transition().duration(200).style('opacity', 0.9);
    //   testCard
    //     .html(formatTime(d.date) + '<br/>' + d[series] + 'test')
    //     .style('left', d3.event.pageX + 'px')
    //     .style('top', d3.event.pageY - 28 + 'px');
    // })
    // .on('mouseout', function (d) {
    //   testCard.transition().duration(500).style('opacity', 0);
    // });
  }

  /**
   * This utility method us used to create an D3 line series constructor
   * This method relies on the calcy() utility method to create a stacked line chart
   * @param  {array} xField [field to be used for x value ]
   * @param  {array} yField [field to be used for y value ]
   * @param  {object} x [x scale]
   * @param  {object} y [y scale]
   * @return {function}          [returns a D3 line constructor]
   */
  createLine(xField, yField, x, y) {
    const xAxisFormat = this.xAxis.format || 'mm/dd/yyyy';

    return (
      d3
        .line()
        // .curve(d3.curveBasis)
        .defined(d => d[yField] !== null)
        .x(d => {
          return x(new Date(`${d[xField]} 00:00:00`));
        })
        .y(d => y(d[yField]))
    );
  }

  /**
   * This method is used to resize the SVG after the heatmap cards have rendered
   * @return {object} [returns Heatmap]
   */
  resize() {
    const svg = document.querySelector(`${this.container} svg g`);
    const container = document.querySelector(this.container);
    const height = svg.getBBox().height;
    container.style.height = `${height}px`;
    return this;
  }

  /**
   * This method is used to draw the heatmap
   * @return {object} [returns Heatmap]
   */

  draw() {
    this.validateConfig()
      .createSVG()
      .createScales()
      .createAxis('x', 'rdate', 'x-axis axis')
      .createAxis('y0', 'y-axis axis')
      .createAxis('y1', 'y-axis axis')
      .createSeries();
    return this;
  }
}
